import type { StorefrontProduct } from '../../api/client';
import { orderable } from '../../commerce/variantStock';

// The choices a buyer makes before buying — Size: 30ml, 50ml — and which variant a set of picks means. Kept
// apart from the controls that draw them, because which pick leads to which variant is a rule, not a layout.

/** One variant of a product. */
type Variant = StorefrontProduct['variants'][number];

/** One choice the buyer makes: its name, and the values offered for it. */
export type ProductOption = { name: string; values: string[] };

/** The most choices a variant can carry, matching its option1 and option2. */
const MAX_OPTIONS = 2;

// Reads whether the product declares its choices, so variants are matched on option1/option2 rather than on their names.
const declares = (product: StorefrontProduct): boolean => (product.options ?? []).some((option) => option.name && option.values.length > 0);

// Lists the choices a buyer makes: the product's declared options, or one "Option" over its variants' names.
export function productOptions(product: StorefrontProduct): ProductOption[] {
  if (declares(product)) return (product.options ?? []).filter((option) => option.name && option.values.length > 0).slice(0, MAX_OPTIONS);
  return product.variants.length > 1 ? [{ name: 'Option', values: product.variants.map((variant) => variant.title) }] : [];
}

// Reads the value one variant holds for each of the product's choices.
export function variantValues(product: StorefrontProduct, variant: Variant): string[] {
  if (!declares(product)) return [variant.title];
  return [variant.option1, variant.option2].slice(0, productOptions(product).length).map((value) => value ?? '');
}

// Finds the variant whose values are exactly the picks, or null when no variant matches all of them.
export function variantFor(product: StorefrontProduct, picks: readonly string[]): Variant | null {
  return product.variants.find((variant) => variantValues(product, variant).every((value, index) => value === picks[index])) ?? null;
}

// Reports whether picking a value at one position, keeping the other picks, leads to a variant in stock.
export function valueInStock(product: StorefrontProduct, picks: readonly string[], index: number, value: string): boolean {
  const next = picks.map((pick, at) => (at === index ? value : pick));
  return orderable(variantFor(product, next) ?? undefined);
}
